import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ErrorType } from 'src/app/core/routing/error-types';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { ToastService } from 'src/app/base/services/toast.service';
import {
  AssignSamplingProfileDTO,
  CreateSamplingProfileConfigDTO,
  FilterDTO,
  IPagedResultDTOOfSamplingProfileConfigDTO,
  SamplingProfileClient,
  SamplingProfileConfigDTO,
  SortDirection,
  UpdateSamplingProfileConfigDTO,
  VehicleListDTO,
  VehicleSignalAvailabilityDTO,
} from 'src/app/base/models/models.swagger.generated';
import { SamplingProfileEditModel } from './sampling-profile-dynamic.definition';

@Injectable()
export class SamplingProfilesService extends ShowInactiveService implements IAdaptorService {

  constructor(
    private samplingProfileClient: SamplingProfileClient,
    private toastService: ToastService,
  ) {
    super();
  }

  public getData(
    skip: number,
    take: number,
    filters: FilterDTO[],
    sortBy: string | undefined,
    sortDirection: SortDirection | undefined,
  ): Observable<IPagedResultDTOOfSamplingProfileConfigDTO | null> {
    return this.samplingProfileClient.searchSamplingProfiles(filters, skip, take, sortBy, sortDirection)
      .pipe(catchError((error: any) => this.handleError(error)));
  }

  public addRecord(data: CreateSamplingProfileConfigDTO): Observable<SamplingProfileConfigDTO | null> {
    return this.samplingProfileClient.createSamplingProfile(data)
      .pipe(
        tap(() => this.toastService.showSuccess(
          'i18n.domain.sampling-profiles.messages.add-title',
          'i18n.domain.sampling-profiles.messages.add-success-message')),
        catchError((error: any) => this.handleError(error)),
      );
  }

  /**
   * `signals` is the complete desired signal list — signals omitted from it are removed from the
   * profile. The scope (`profileType`) is deliberately not sent: it is immutable after creation.
   */
  public updateRecord(data: SamplingProfileEditModel): Observable<SamplingProfileConfigDTO | null> {
    const payload = new UpdateSamplingProfileConfigDTO({
      id: data.id,
      name: data.name,
      description: data.description ?? undefined,
      signals: data.signals ?? [],
    });

    return this.samplingProfileClient.updateSamplingProfile(payload)
      .pipe(
        tap(() => this.toastService.showSuccess(
          'i18n.domain.sampling-profiles.messages.update-title',
          'i18n.domain.sampling-profiles.messages.update-success-message')),
        catchError((error: any) => this.handleError(error)),
      );
  }

  public deleteRecord(id: number): Observable<any> {
    return this.samplingProfileClient.deleteSamplingProfile(id)
      .pipe(
        tap(() => this.toastService.showSuccess(
          'i18n.domain.sampling-profiles.messages.delete-title',
          'i18n.domain.sampling-profiles.messages.delete-success-message')),
        catchError((error: any) => this.handleError(error)),
      );
  }

  /** Single profile including its full signal list — the grid search omits signals to stay cheap. */
  public getProfile(id: number): Observable<SamplingProfileConfigDTO | null> {
    return this.samplingProfileClient.getSamplingProfile(id)
      .pipe(catchError((error: any) => this.handleError(error)));
  }

  /**
   * Copies a profile into a new user-owned one. The supported way to customise a shared System
   * profile without changing collection for every vehicle using it.
   */
  public cloneProfile(sourceProfileId: number, name: string, description?: string): Observable<SamplingProfileConfigDTO | null> {
    return this.samplingProfileClient.cloneSamplingProfile(sourceProfileId, name, description)
      .pipe(
        tap(() => this.toastService.showSuccess(
          'i18n.domain.sampling-profiles.messages.clone-title',
          'i18n.domain.sampling-profiles.messages.clone-success-message')),
        catchError((error: any) => this.handleError(error)),
      );
  }

  public assignProfileToVehicle(vehicleId: number, samplingProfileId: number): Observable<void> {
    return this.samplingProfileClient.assignProfileToVehicle(new AssignSamplingProfileDTO({ vehicleId, samplingProfileId }))
      .pipe(
        tap(() => this.toastService.showSuccess(
          'i18n.domain.sampling-profiles.messages.assign-title',
          'i18n.domain.sampling-profiles.messages.assign-success-message')),
        catchError((error: any) => this.handleError(error)),
      );
  }

  public getAssignedVehicles(samplingProfileId: number): Observable<VehicleListDTO[]> {
    return this.samplingProfileClient.getAssignedVehicles(samplingProfileId)
      .pipe(catchError((error: any) => this.handleError(error)));
  }

  /**
   * Which of the vehicle's configured signals the collector actually managed to read. Availability is
   * measured per collector installation, so it is always requested for one specific vehicle.
   */
  public getSignalAvailability(vehicleId: number): Observable<VehicleSignalAvailabilityDTO | null> {
    return this.samplingProfileClient.getSignalAvailability(vehicleId)
      .pipe(catchError((error: any) => this.handleError(error)));
  }

  private handleError(error: any): Observable<never> {
    if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
      this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', '', ErrorType.serverError);
    }
    return throwError(() => error);
  }
}
